import { Fragment, useState } from 'react';

import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon as DotsVerticalIcon } from '@heroicons/react/20/solid';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { QRCodeFileSelectionModal } from '@/components/ProfilesListPage/QRCode';
import { SendPassModal } from '@/components/Templates/DigitalWallet';
import { SendEmailSignatureModal } from '@/components/Templates/ShareWithTeamButton';
import { classNames } from '@/helpers/strings';
import useAuth from '@/hooks/useAuth';
import { IProfileBase } from '@/types/IProfile';

import { AddProfilesToGroupModal } from './AddProfilesToGroupModal';

export function MoreOptionsMenu({ profile }: { profile: IProfileBase }) {
  const { userRole, orgID } = useAuth();
  const queryClient = useQueryClient();

  const [isAddToGroupModalOpen, setIsAddToGroupModalOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [isSendPassModalOpen, setIsSendPassModalOpen] = useState(false);
  const [isSendEmailSignatureModalOpen, setIsSendEmailSignatureModalOpen] =
    useState(false);

  const { id } = profile;

  const menuOptions = [
    {
      title: 'Add to group',
      onClick: () => setIsAddToGroupModalOpen(true),
      visibility: userRole === 'org_admin',
    },
    {
      title: 'Export QR Code',
      onClick: () => setIsQRCodeModalOpen(true),
      visibility: true,
    },
    {
      title: 'Share wallet pass',
      onClick: () => {
        setIsSendPassModalOpen(true);
      },
      visibility: true,
    },
    {
      title: 'Share email signature',
      onClick: () => {
        setIsSendEmailSignatureModalOpen(true);
      },
      visibility: true,
    },
  ];

  return (
    <>
      <Menu as="div" className="relative inline-block items-center">
        <div className="flex items-center">
          <Menu.Button
            onClick={e => e.stopPropagation()}
            className="appearance-none"
          >
            <DotsVerticalIcon className="w-5 h-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            {menuOptions.map((option, index) => (
              <Menu.Item key={index}>
                <button
                  type="button"
                  className={classNames(
                    'text-gray-700 w-full text-left px-4 py-2 text-sm hover:bg-gray-50',
                    option.visibility ? 'block' : 'hidden',
                  )}
                  onClick={e => {
                    e.stopPropagation();
                    option.onClick();
                  }}
                >
                  {option.title}
                </button>
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
      <AddProfilesToGroupModal
        isOpen={isAddToGroupModalOpen}
        setIsOpen={setIsAddToGroupModalOpen}
        checkedProfiles={[profile]}
        onSuccessCallback={() => {
          queryClient.invalidateQueries({
            queryKey: ['listProfilesInGroup', orgID],
          });
          toast.success('Profile was added to group successfully');
        }}
      />
      <QRCodeFileSelectionModal
        checkedProfiles={[id]}
        isOpen={isQRCodeModalOpen}
        setIsOpen={setIsQRCodeModalOpen}
      />
      <SendPassModal
        isOpen={isSendPassModalOpen}
        setIsOpen={setIsSendPassModalOpen}
        checkedProfiles={[id]}
        onSuccessCallback={() => {
          toast.success('Wallet pass was sent successfully');
        }}
      />
      <SendEmailSignatureModal
        isOpen={isSendEmailSignatureModalOpen}
        setIsOpen={setIsSendEmailSignatureModalOpen}
        checkedProfiles={[id]}
        onSuccessCallback={() => {
          toast.success('Email signature was sent successfully');
        }}
      />
    </>
  );
}
